import authClient from '@/utils/auth-client'
import router from "@/router";
import {NewPasswordRequest, PasswordResetRequest, StatusResponse} from "@/model/models";
import apiClient from "@/utils/api-client";
import {resolveDeviceContext} from "@/utils/device-context";
import UsersService from "@/services/users-service";

class AuthService {
  private getWebsite(): string {
    // simplest: hostname, or map to your canonical site string
    return window.location.hostname;
  }
  public async signIn(email: string, password: string): Promise<boolean> {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    // Resolve device BEFORE token request (web + android)
    const device = await resolveDeviceContext();

    const params = new URLSearchParams();
    params.set("grant_type", "password");
    params.set("scope", "API");
    params.set("username", email);
    params.set("password", password);

    params.set("device_identifier", device.device_identifier);
    params.set("device_type", device.device_type);
    params.set("device_os", device.device_os);
    params.set("website", this.getWebsite());
    if (device.user_agent) {
      params.set("user_agent", device.user_agent);
    }
    if (device.device_name) {
      params.set("device_name", device.device_name);
    }

    const res = await authClient.post("/token", params.toString(), {
      headers: { "Content-Type": "application/x-www-form-urlencoded" }
    });

    const token = res.data.access_token;
    const expiresIn = (res.data.expires_in | 0) * 1000;

    if (!token || expiresIn <= 0) {
      throw new Error("Invalid access token");
    }

    localStorage.setItem("access_token", token);
    localStorage.setItem("refresh_token", res.data.refresh_token);

    return true;
  }

  public refreshToken() {
    return new Promise<boolean>((resolve, reject) => {
      const requestBody = 'grant_type=refresh_token&refresh_token=' + localStorage.getItem('refresh_token')

      authClient.post('/token', requestBody).then((res) => {
        const token = res.data.access_token
        const expiresIn = (res.data.expires_in | 0) * 1000

        if (token && token.length && expiresIn > 0) {
          localStorage.setItem('access_token', token)
          localStorage.setItem('refresh_token', res.data.refresh_token)

          resolve(true)
        } else {
          reject(new Error('Invalid refresh token'))
        }
      }).catch(() => {
          localStorage.clear()
          localStorage.setItem('sessionExpired', "true")
          const current = window.location.pathname + window.location.search + window.location.hash
          if (current.startsWith('/signin')) { return }
          if (window.location.pathname === '/player') {
            router.push({ path: '/signin' })
            return
          }
          router.push({path: '/signin', query: { redirect: current }})
      })
    })
  }

  public initPasswordReset(passwordResetRequest: PasswordResetRequest) {
    return new Promise<StatusResponse>((resolve, reject) => {
      apiClient.post('/api/v1/auth/init-reset', passwordResetRequest).then((res) => {
        if (res.status === 200 && res.data !== null) {
          resolve(new StatusResponse(res.data.code, res.data.userId, res.data.message, res.data.timeStamp))
        } else {
          reject(new Error(res.data?.message))
        }
      }).catch((error) => {
        reject(error)
      })
    })
  }

  public resetPassword(newPasswordRequest: NewPasswordRequest) {
    return new Promise<StatusResponse>((resolve, reject) => {
      apiClient.post('/api/v1/auth/reset-password', newPasswordRequest).then((res) => {
        if (res.status === 200 && res.data !== null) {
          resolve(new StatusResponse(res.data.code, res.data.userId, res.data.message, res.data.timeStamp))
        } else if (res.status === 208) {
          reject(new Error("The link is expired, or you already reset your password. Please request a new password reset."))
        } else {
          reject(new Error("An error occurred. Please try again later."))
        }
      }).catch((error) => {
        reject(error)
      })
    })
  }

  public signOut() {
    return new Promise<boolean>((resolve) => {
      localStorage.clear()
      sessionStorage.clear()
      resolve(true)
    })
  }

  public isSignedIn() : boolean {
    const token = localStorage.getItem('access_token')

    return token !== null && token.length > 0
  }

  public async ensureSignedIn(): Promise<boolean> {
    const token = localStorage.getItem("access_token");
    if (!token) return false;

    try {
      await UsersService.getUserLatest(); // validates token
      return true;
    } catch (e) {
      // token invalid / expired
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      return false;
    }
  }
}

export default new AuthService()
